import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import { Box, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { TransientFlowFixedRadiusResult } from "../../types/interfaces/methods";

interface TransientFlowFixedRadiusResultsProps {
  calculations: TransientFlowFixedRadiusResult[];
}

export default function TransientFlowFixedRadiusResults({
  calculations,
}: Readonly<TransientFlowFixedRadiusResultsProps>) {
  const { t } = useTranslation();

  const generateRows = (category: "fluxoTempoTransito" | "raioFixoVolumetrica" | "raioFixoRecarga") => {
    return calculations.map((calc, index) => {
      const r = calc.results;
      if (category === "fluxoTempoTransito") {
        return {
          id: index,
          fttVe: r.fluxoTempoTransito.fttVe.toFixed(2),
          fttD1dia: r.fluxoTempoTransito.fttD1dia.toFixed(2),
          fttD50dias: r.fluxoTempoTransito.fttD50dias.toFixed(2),
          fttD3500dias: r.fluxoTempoTransito.fttD3500dias.toFixed(2),
        };
      } else if (category === "raioFixoVolumetrica") {
        return {
          id: index,
          rfvR1dia: r.raioFixoVolumetrica.rfvR1dia.toFixed(2),
          rfvR50dias: r.raioFixoVolumetrica.rfvR50dias.toFixed(2),
          rfvR3500dias: r.raioFixoVolumetrica.rfvR3500dias.toFixed(2),
        };
      } else {
        return {
          id: index,
          rrrR1dia: r.raioFixoRecarga.rrrR1dia.toFixed(2),
        };
      }
    });
  };

  const fluxoColumns: GridColDef[] = [
    { field: "fttVe", headerName: t("calculations.methods.transient-flow-fixed-radius.results.fttVe"), flex: 1 },
    { field: "fttD1dia", headerName: t("calculations.methods.transient-flow-fixed-radius.results.fttD1dia"), flex: 1 },
    { field: "fttD50dias", headerName: t("calculations.methods.transient-flow-fixed-radius.results.fttD50dias"), flex: 1 },
    { field: "fttD3500dias", headerName: t("calculations.methods.transient-flow-fixed-radius.results.fttD3500dias"), flex: 1 },
  ];

  const volumetricaColumns: GridColDef[] = [
    { field: "rfvR1dia", headerName: t("calculations.methods.transient-flow-fixed-radius.results.rfvR1dia"), flex: 1 },
    { field: "rfvR50dias", headerName: t("calculations.methods.transient-flow-fixed-radius.results.rfvR50dias"), flex: 1 },
    { field: "rfvR3500dias", headerName: t("calculations.methods.transient-flow-fixed-radius.results.rfvR3500dias"), flex: 1 },
  ];

  const recargaColumns: GridColDef[] = [
    { field: "rrrR1dia", headerName: t("calculations.methods.transient-flow-fixed-radius.results.rrrR1dia"), flex: 1 },
  ];

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
        {t("calculations.methods.results-title")}
      </Typography>

      {/* Fluxo Tempo Transito */}
      <Box sx={{ mb: 5 }}>
        <Typography 
          variant="body1" 
          color="text.secondary" 
          sx={{ fontWeight: 700, mt: 3, mb: 1, display: "flex", alignItems: "center", gap: 1, }}
        >
          <RadioButtonCheckedIcon sx={{ fontSize: 18 }} /> 
          {t("calculations.methods.transient-flow-fixed-radius.fluxo-tempo-transito")} 
        </Typography>
        <DataGrid
          rows={generateRows("fluxoTempoTransito")}
          columns={fluxoColumns}
          hideFooter
          disableColumnMenu
          sx={{
            boxShadow: 1,
          }}
        />
      </Box>

      {/* Raio Fixo Volumetrica */}
      <Box sx={{ mb: 5 }}>
        <Typography 
          variant="body1" 
          color="text.secondary" 
          sx={{ fontWeight: 700, mt: 3, mb: 1, display: "flex", alignItems: "center", gap: 1, }}
        >
          <RadioButtonCheckedIcon sx={{ fontSize: 18 }} /> 
          {t("calculations.methods.transient-flow-fixed-radius.raio-fixo-volumetrica")}
        </Typography>
        <DataGrid
          rows={generateRows("raioFixoVolumetrica")}
          columns={volumetricaColumns}
          hideFooter
          disableColumnMenu
          sx={{
            boxShadow: 1,
          }}
        />
      </Box>

      {/* Raio Fixo Recarga */}
      <Box sx={{ mb: 5 }}>
        <Typography 
          variant="body1" 
          color="text.secondary" 
          sx={{ fontWeight: 700, mt: 3, mb: 1, display: "flex", alignItems: "center", gap: 1, }}
        >
          <RadioButtonCheckedIcon sx={{ fontSize: 18 }} /> 
          {t("calculations.methods.transient-flow-fixed-radius.raio-fixo-recarga")}
        </Typography>
        <DataGrid
          rows={generateRows("raioFixoRecarga")}
          columns={recargaColumns}
          hideFooter
          disableColumnMenu
          sx={{
            boxShadow: 1,
          }}
        />
      </Box>
    </Box>
  );
}